// 「不合适 3 天内复盘 / 有好感约第二次」 → 已约会过的推荐对象: each person this
// member has already met, how many dates so far, and 「安排第 N 次约会」
// (N = dates so far + 1, no limit). The dialog writes a dating_schedules row
// (date + time on the Pacific clock, location, note) and the backend tells
// BOTH members' 售后红娘 (bell + email). Hidden while the API is not deployed.
import React, { useCallback, useEffect, useState } from 'react';
import { CalendarPlus } from 'lucide-react';
import { toast } from 'react-toastify';
import { stepExtrasAPI } from '../../../services/api';
import { laTodayYMD } from '../../../utils/laTime';
import { S, card, btnPrimary, btnGhost, btnLink, field } from './wsShared';

const MATCH_TAG = {
  in_relationship: ['已交往', 'Dating', { background: '#3F7D5A', color: '#FFFDF9' }],
  succeeded: ['已约见', 'Met', { background: '#C2671E', color: '#FFFDF9' }],
  in_contact: ['接触中', 'In contact', { background: '#E6EFE9', color: '#2F5E44' }],
};
const md = (ymd) => (ymd ? `${String(ymd).slice(5, 7)}/${String(ymd).slice(8, 10)}` : '');

export default function NextDatePanel({ profileId, memberName = '', isZh = true, TimeInput = null, onChanged }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [list, setList] = useState(null);   // null = loading / unavailable
  const [gone, setGone] = useState(false);  // API missing → render nothing
  const [dlg, setDlg] = useState(null);     // { partner, date, time, location, note }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await stepExtrasAPI.datedPartners(profileId);
      setList(Array.isArray(r.data?.partners) ? r.data.partners : []);
      setGone(false);
    } catch (e) {
      setList(null); setGone(true);
    }
  }, [profileId]);
  useEffect(() => { load(); }, [load]);

  if (gone || !list) return null;

  const save = async () => {
    const d = dlg;
    if (!d.date) { toast.error(t('请选择日期', 'Pick a date')); return; }
    if (!d.time) { toast.error(t('请选择时间（美西）', 'Pick a time (PT)')); return; }
    setBusy(true);
    try {
      const r = await stepExtrasAPI.createNextDate(profileId, {
        partner_id: d.partner.partner_id, scheduled_date: d.date, scheduled_time: d.time,
        location: d.location.trim() || undefined, note: d.note.trim() || undefined,
      });
      const n = r.data?.schedule?.date_round || d.partner.next_round;
      toast.success(t(`已安排第 ${n} 次约会，已通知双方售后红娘`, `Date #${n} scheduled`));
      setDlg(null);
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || t('保存失败', 'Could not save'));
    } finally { setBusy(false); }
  };

  const lbl = { fontSize: 12.5, color: S.sub, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 };
  return (
    <div style={{ border: `1px solid ${S.line}`, borderRadius: 2, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, background: '#FFFDF9' }}>
      <div style={{ fontSize: 12, letterSpacing: '.2em', color: '#8A6D3B' }}>{t('已约会过的推荐对象', 'People already met')}</div>
      {!list.length ? (
        <div style={{ fontSize: 13, color: S.sub }}>{t('还没有已经见过面的对象。', 'No dates held yet.')}</div>
      ) : list.map((p) => {
        const tag = MATCH_TAG[p.match_status];
        return (
          <div key={p.partner_id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 0', borderTop: `1px solid ${S.line}` }}>
            <div style={{ flex: '1 1 180px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 14, color: S.ink, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {p.name}{p.offline_id && <span style={{ color: S.sub, fontSize: 12.5 }}>{t(`线下 ID ${p.offline_id}`, `ID ${p.offline_id}`)}</span>}
                {tag && <span style={{ fontSize: 12, padding: '2px 8px', ...tag[2] }}>{isZh ? tag[0] : tag[1]}</span>}
              </span>
              <span style={{ fontSize: 12.5, color: S.sub }}>
                {t(`已约 ${p.dates} 次`, `${p.dates} date${p.dates > 1 ? 's' : ''}`)}
                {p.last ? t(` · 最近 ${md(p.last.scheduled_date)}`, ` · last ${md(p.last.scheduled_date)}`) : ''}
                {p.upcoming ? t(` · 下一次 ${md(p.upcoming.scheduled_date)} ${p.upcoming.scheduled_time || ''}`, ` · next ${md(p.upcoming.scheduled_date)}`) : ''}
              </span>
            </div>
            <button type="button" onClick={() => setDlg({ partner: p, date: '', time: '', location: (p.last && p.last.location) || '', note: '' })}
              style={{ ...btnGhost, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <CalendarPlus size={14} strokeWidth={1.5} aria-hidden="true" />{t(`安排第 ${p.next_round} 次约会`, `Schedule date #${p.next_round}`)}
            </button>
          </div>
        );
      })}

      {dlg && (
        <div role="dialog" aria-modal="true" onClick={() => !busy && setDlg(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(30,20,20,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 12, background: '#FFFDF9', boxSizing: 'border-box' }}>
            <div>
              <div style={{ fontFamily: S.serif, fontSize: 22, fontWeight: 500, color: S.ink }}>{t(`安排第 ${dlg.partner.next_round} 次约会`, `Schedule date #${dlg.partner.next_round}`)}</div>
              <div style={{ fontSize: 13, color: S.sub, marginTop: 2 }}>{`${memberName || '—'} × ${dlg.partner.name}`}</div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ ...lbl, flex: '1 1 150px' }}>{t('日期', 'Date')}
                <input type="date" value={dlg.date} min={laTodayYMD()} onChange={(e) => setDlg((x) => ({ ...x, date: e.target.value }))} style={field} />
              </label>
              <div style={{ ...lbl, flex: '1 1 150px' }}>{t('时间（美西 · 可选可填）', 'Time (PT)')}
                {TimeInput
                  ? <TimeInput value={dlg.time} onChange={(v) => setDlg((x) => ({ ...x, time: v }))} isZh={isZh} placeholder={t('选择时间', 'Time')} style={field} />
                  : <input type="time" value={dlg.time} onChange={(e) => setDlg((x) => ({ ...x, time: e.target.value }))} style={field} />}
              </div>
            </div>
            <label style={lbl}>{t('地点', 'Location')}
              <input value={dlg.location} maxLength={500} onChange={(e) => setDlg((x) => ({ ...x, location: e.target.value }))} style={field} />
            </label>
            <label style={lbl}>{t('备注（只给红娘看）', 'Note (staff only)')}
              <textarea value={dlg.note} rows={3} maxLength={2000} onChange={(e) => setDlg((x) => ({ ...x, note: e.target.value }))} style={{ ...field, height: 'auto', padding: 8, resize: 'vertical' }} />
            </label>
            <div style={{ fontSize: 12.5, color: S.sub, lineHeight: 1.6, background: '#EFEAE2', padding: '8px 10px', borderRadius: 2 }}>
              {t('保存后会通知双方会员各自的售后红娘（站内 + 邮件），并出现在她们今日跟进的「约会提醒」里。', 'Both members’ coaches are notified (in-app + email) and see it under 约会提醒.')}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              <button type="button" disabled={busy} onClick={() => setDlg(null)} style={{ ...btnLink, color: S.sub }}>{t('取消', 'Cancel')}</button>
              <button type="button" disabled={busy} onClick={save} style={btnPrimary(busy)}>{busy ? t('保存中…', 'Saving…') : t('保存并通知', 'Save and notify')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
